import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Modal, Button } from '@patternfly/react-core';

type SessionTimeoutProps = {
	sessionExpiryTime: number; // milliseconds
	extendSession: () => void;
	logout: () => void;
};

export const SessionTimeout: React.FC<SessionTimeoutProps> = ({
	sessionExpiryTime,
	extendSession,
	logout,
}) => {
	const [showModal, setShowModal] = useState(false);
	const [remaining, setRemaining] = useState(0);

	const lastActivityRef = useRef<number>(Date.now());
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const countdownRef = useRef<NodeJS.Timeout | null>(null);

	// --- Track user activity ---
	const resetTimer = useCallback(() => {
		if (showModal) return; // ignore activity during countdown
		lastActivityRef.current = Date.now();
		scheduleCheck();
	}, [showModal, sessionExpiryTime]);

	useEffect(() => {
		const events = ['mousemove', 'keydown', 'click', 'scroll'];
		events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));

		return () => {
			events.forEach((e) => window.removeEventListener(e, resetTimer));
		};
	}, [resetTimer]);

	// --- Schedule inactivity check ---
	const scheduleCheck = useCallback(() => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current);

		const elapsed = Date.now() - lastActivityRef.current;
		const remainingMs = sessionExpiryTime - elapsed;

		if (remainingMs <= 0) {
			startCountdown();
		} else {
			timeoutRef.current = setTimeout(startCountdown, remainingMs);
		}
	}, [sessionExpiryTime]);

	useEffect(() => {
		scheduleCheck();

		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			if (countdownRef.current) clearInterval(countdownRef.current);
		};
	}, [scheduleCheck]);

	// --- Start countdown modal ---
	const startCountdown = useCallback(() => {
		if (showModal) return; // prevent double start
		setShowModal(true);
		const COUNTDOWN_SECONDS = 0.11 * 60; // 5 min countdown
		setRemaining(COUNTDOWN_SECONDS);

		countdownRef.current = setInterval(() => {
			setRemaining((prev) => {
				if (prev <= 1) {
					clearInterval(countdownRef.current!);
					localStorage.setItem('hasSessionInactivityTimeout', '1');
					handleLogout();
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, [showModal]);

	const handleExtend = () => {
		extendSession();
		setShowModal(false);
		if (countdownRef.current) clearInterval(countdownRef.current);
		lastActivityRef.current = Date.now();
		scheduleCheck();
	};

	const handleLogout = () => {
		setShowModal(false);
		if (countdownRef.current) clearInterval(countdownRef.current);
		logout();
	};

	const formatTime = (seconds: number) => {
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
	};

	return (
		<Modal
			title="Session Expiring"
			isOpen={showModal}
			showClose={false}
			variant="small"
			actions={[
				<Button
					key="continue"
					variant="primary"
					onClick={handleExtend}
				>
					Continue Session
				</Button>,
				<Button
					key="logout"
					variant="secondary"
					onClick={handleLogout}
				>
					Logout
				</Button>,
			]}
		>
			<p>Your session will expire in {formatTime(remaining)} minutes.</p>
			<p>Do you want to continue your session?</p>
		</Modal>
	);
};
